import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Alert,
    Card,
    Checkbox,
    IconButton,
} from '@mui/material'
import {
    Hive as HiveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material'
import SaveToolbar from '@components/common/SaveToolbar'
import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { createGroup } from '@store/slices/groupsSlice'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { groupsApi } from '@api/groups'
import { Group } from '@types'
import ColorPicker from '@components/common/ColorPicker'

interface PendingInvite {
    id: number // Temporary ID for UI
    name: string
    email: string
    isAdmin: boolean
}

const GroupCreate: React.FC = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { apiaries, loadingState: locationsLoading } = useAppSelector((state) => state.locations)
    const { user } = useAppSelector((state) => state.auth)

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [hexColor, setHexColor] = useState('#F8B133')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Selected hives state: { [hiveId]: 'view' | 'edit' }
    const [selectedHives, setSelectedHives] = useState<Record<number, 'view' | 'edit'>>({})

    // Pending invitations
    const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])

    useEffect(() => {
        if (locationsLoading === 'idle') {
            dispatch(fetchApiariesAndGroups())
        }
    }, [dispatch, locationsLoading])

    const handleHiveClick = (hiveId: number) => {
        setSelectedHives(prev => {
            const current = prev[hiveId]
            if (!current) return { ...prev, [hiveId]: 'view' }
            if (current === 'view') return { ...prev, [hiveId]: 'edit' }
            // If edit, remove it (toggle off)
            const newState = { ...prev }
            delete newState[hiveId]
            return newState
        })
    }

    const handleAddMember = () => {
        setPendingInvites(prev => [
            ...prev,
            {
                id: Date.now(), // Simple temp ID
                name: '',
                email: '',
                isAdmin: false
            }
        ])
    }

    const handleRemoveMember = (id: number) => {
        setPendingInvites(prev => prev.filter(invite => invite.id !== id))
    }

    const handleInviteChange = (id: number, field: keyof PendingInvite, value: any) => {
        setPendingInvites(prev => prev.map(invite =>
            invite.id === id ? { ...invite, [field]: value } : invite
        ))
    }

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Group name is required')
            return
        }

        setLoading(true)
        setError(null)

        try {
            // 1. Create Group
            const groupData: Partial<Group> = {
                name,
                description,
                hex_color: hexColor,
                is_public: false, // Default to private
            }

            const resultAction = await dispatch(createGroup(groupData))

            if (createGroup.fulfilled.match(resultAction)) {
                const newGroup = resultAction.payload

                // 2. Share selected hives
                const sharePromises = Object.entries(selectedHives).map(([hiveId, permission]) => {
                    return groupsApi.shareHiveWithGroup(newGroup.id, {
                        hive_id: parseInt(hiveId),
                        can_edit: permission === 'edit',
                        can_view_details: true,
                    })
                })

                // 3. Send invitations
                const invitePromises = pendingInvites
                    .filter(invite => invite.email.trim()) // Only send if email is present
                    .map(invite => {
                        // Note: API currently only takes email. Name and isAdmin might need backend update or separate call.
                        // For now, we just invite by email.
                        return groupsApi.inviteUser(newGroup.id, invite.email)
                    })

                await Promise.all([...sharePromises, ...invitePromises])

                navigate('/groups')
            } else {
                setError('Failed to create group')
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
            <SaveToolbar
                onSave={handleSave}
                saveDisabled={!name.trim() || loading}
                loading={loading}
                showDelete={false}
            />

            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
                New Group
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Card sx={{ mb: 4 }}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="overline" display="block" gutterBottom color="text.secondary">
                        Basic information
                    </Typography>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: { xs: 2, md: 3 } }}>
                        <TextField
                            fullWidth
                            label="Group Name"
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, 30))}
                            helperText={`${name.length}/30`}
                            sx={{ mb: 3 }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, 250))}
                            multiline
                            rows={3}
                            helperText={`${description.length}/250`}
                            sx={{ mb: 3 }}
                        />
                        <Typography variant="subtitle2" gutterBottom>
                            Group color
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
                            <ColorPicker color={hexColor} onChange={setHexColor} />
                            <Box
                                sx={{
                                    minWidth: 200,
                                    border: `2px solid ${hexColor}`,
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {name || 'Your group name'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Collaboration group
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Card>

                {/* Group Members */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                        GROUP MEMBERS ({1 + pendingInvites.length})
                    </Typography>
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>E-mail</TableCell>
                                    <TableCell>Invited</TableCell>
                                    <TableCell>Type member</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Current User Row */}
                                <TableRow>
                                    <TableCell>1</TableCell>
                                    <TableCell>{user?.full_name || user?.first_name || 'Current User'}</TableCell>
                                    <TableCell>{user?.email || 'user@example.com'}</TableCell>
                                    <TableCell>-</TableCell>
                                    <TableCell>Group owner</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>

                                {/* Pending Invites Rows */}
                                {pendingInvites.map((invite, index) => (
                                    <TableRow key={invite.id}>
                                        <TableCell>{index + 2}</TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                placeholder="Invitee name"
                                                value={invite.name}
                                                onChange={(e) => handleInviteChange(invite.id, 'name', e.target.value)}
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                placeholder="Please enter an email address"
                                                value={invite.email}
                                                onChange={(e) => handleInviteChange(invite.id, 'email', e.target.value)}
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    checked={invite.isAdmin}
                                                    onChange={(e) => handleInviteChange(invite.id, 'isAdmin', e.target.checked)}
                                                    color="default"
                                                />
                                                <Typography variant="body2">Administrator</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveMember(invite.id)}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleAddMember}
                            sx={{ color: '#F8B133', borderColor: '#F8B133' }}
                        >
                            + ADD MEMBER
                        </Button>
                    </Box>
                </Box>

                {/* Shared Hives */}
                <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'uppercase', color: 'text.secondary' }}>
                        MY SHARED HIVES
                    </Typography>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select hives to share with this group. 1 click = group members can view only, 2 clicks = group members can edit
                        </Typography>

                        <Box sx={{ mt: 3 }}>
                            {apiaries.map(apiary => (
                                <Box key={apiary.id} sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: '#F8B133' }}>
                                        <HiveIcon />
                                        <Typography variant="h6" color="inherit">
                                            {apiary.name}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        {apiary.hives.map(hive => {
                                            const status = selectedHives[hive.id]
                                            return (
                                                <Tooltip
                                                    key={hive.id}
                                                    title={status === 'edit' ? 'Can Edit' : status === 'view' ? 'View Only' : 'Click to share'}
                                                >
                                                    <Box
                                                        onClick={() => handleHiveClick(hive.id)}
                                                        sx={{
                                                            width: 60,
                                                            height: 80,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Box sx={{
                                                            width: 50,
                                                            height: 50,
                                                            bgcolor: status ? '#F8B133' : '#e0e0e0',
                                                            borderRadius: 1,
                                                            border: status === 'edit' ? '3px solid #e65100' : '1px solid #bdbdbd',
                                                            transition: 'all 0.2s',
                                                            position: 'relative',
                                                            '&:hover': {
                                                                bgcolor: status ? '#F9C25C' : '#eeeeee'
                                                            }
                                                        }}>
                                                            {/* Horizontal lines to look like hive box */}
                                                            <Box sx={{ position: 'absolute', top: '33%', left: 0, right: 0, height: 1, bgcolor: 'rgba(0,0,0,0.1)' }} />
                                                            <Box sx={{ position: 'absolute', top: '66%', left: 0, right: 0, height: 1, bgcolor: 'rgba(0,0,0,0.1)' }} />
                                                        </Box>
                                                        <Typography variant="caption" align="center" sx={{ lineHeight: 1.2 }}>
                                                            {hive.name}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            )
                                        })}
                                    </Box>
                                    <Box sx={{ mt: 2, borderBottom: '1px solid #F8B133' }} />
                                </Box>
                            ))}

                            {apiaries.length === 0 && (
                                <Typography color="textSecondary" align="center">
                                    No apiaries found. Create an apiary and hives first.
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Box>
        </Container>
    )
}

export default GroupCreate
